import React, {useCallback, useState, useEffect} from "react";
import {ExclamationCircleOutlined, LockOutlined, CheckCircleOutlined} from "@ant-design/icons";
import {useUpdatePasswordMutation} from "../services/nestjs-base-api";
import {Button, Form, Input, message, Typography} from "antd";
import {useParams} from "react-router-dom";
import { useIntl } from "react-intl";
import ModernAuthLayout from "../components/Auth/ModernAuthLayout";

const { Text, Title, Link } = Typography;

const ResetPasswordPage: React.FunctionComponent = () => {
    const intl = useIntl();
    const [updatePassword, updatePasswordResult] = useUpdatePasswordMutation();
    const [messageApi, contextHolder] = message.useMessage();
    const [hideForm, setHideForm] = useState<boolean>(false);
    const { resetToken } = useParams();
    const [isMobile, setIsMobile] = useState(false);

    useEffect(() => {
        const checkMobile = () => {
            setIsMobile(window.innerWidth < 768);
        };
        checkMobile();
        window.addEventListener('resize', checkMobile);
        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    // Normalize the token from URL params to handle encoding issues
    const normalizedToken = resetToken?.trim();

    const onUpdateError = useCallback((error?: any) => {
        console.error('[PasswordReset] Update failed:', error);
        messageApi.open({
            type: 'error',
            content: intl.formatMessage({ id: 'auth.passwordReset.resetError' }),
            duration: 4,
            icon: <ExclamationCircleOutlined />
        });
    }, [messageApi, intl]);

    const handleUpdatePassword = useCallback(async (values: any) => {
        // Normalize password to handle Unicode encoding differences (Mac vs Windows)
        const normalizedPassword = values.password?.normalize('NFC');

        if (normalizedPassword !== values.password) {
            console.log('[PasswordReset] Password was normalized from NFD to NFC');
        }

        const updateResponse = await updatePassword({
            token: normalizedToken!,
            password: normalizedPassword
        });


        if ('data' in updateResponse) {
            console.log('[PasswordReset] Success!');
            setHideForm(true);
        } else {
            console.error('[PasswordReset] Error response:', updateResponse.error);
            onUpdateError(updateResponse.error);
        }
    }, [onUpdateError, normalizedToken, updatePassword]);

    // Inline SVG logo component for crisp rendering
    const LogoIcon = ({ size = 48 }: { size?: number }) => (
        <svg width={size} height={size} viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="24" cy="24" r="20" stroke="#3B82F6" strokeWidth="2" opacity="0.3"/>
            <circle cx="24" cy="24" r="10" fill="#F59E0B"/>
            <circle cx="24" cy="4" r="4" fill="#3B82F6"/>
            <circle cx="6" cy="34" r="4" fill="#22C55E"/>
            <circle cx="42" cy="34" r="4" fill="#3B82F6"/>
        </svg>
    );

    const resetLeftContent = (
        <div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px', marginBottom: '2rem' }}>
                <LogoIcon size={48} />
                <span style={{ fontSize: '32px', fontWeight: '700' }}>
                    <span style={{ color: 'white' }}>BrandScience</span>
                    <span style={{ color: 'rgba(255,255,255,0.6)' }}>.ai</span>
                </span>
            </div>
            <h1 style={{ fontSize: '3rem', fontWeight: '700', marginBottom: '1.5rem', lineHeight: '1.2' }}>
                {intl.formatMessage({ id: 'auth.passwordReset.heading' })}
            </h1>
            <p style={{ fontSize: '1.25rem', opacity: 0.9, lineHeight: '1.6', marginBottom: '2rem' }}>
                {intl.formatMessage({ id: 'auth.passwordReset.description' })}
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                    <div style={{ width: '20px', height: '20px', background: 'rgba(255,255,255,0.2)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>🔐</div>
                    <span>{intl.formatMessage({ id: 'auth.passwordReset.requirement.length' })}</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                    <div style={{ width: '20px', height: '20px', background: 'rgba(255,255,255,0.2)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>💪</div>
                    <span>{intl.formatMessage({ id: 'auth.passwordReset.requirement.mix' })}</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                    <div style={{ width: '20px', height: '20px', background: 'rgba(255,255,255,0.2)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>🛡️</div>
                    <span>{intl.formatMessage({ id: 'auth.passwordReset.requirement.unique' })}</span>
                </div>
            </div>
        </div>
    );

    return (
        <ModernAuthLayout leftContent={resetLeftContent}>
            {contextHolder}
            {!hideForm ? (
                <>
                    <div style={{ marginBottom: isMobile ? '1.5rem' : '2rem', textAlign: 'center' }}>
                        <Title level={2} style={{ color: '#1f2937', fontWeight: '700', marginBottom: '0.5rem', fontSize: isMobile ? '24px' : '30px' }}>
                            {intl.formatMessage({ id: 'auth.passwordReset.title' })}
                        </Title>
                        <Text style={{ color: '#6b7280', fontSize: isMobile ? '14px' : '16px' }}>
                            {intl.formatMessage({ id: 'auth.passwordReset.instructionText' })}
                        </Text>
                    </div>

                    <Form
                        name="password_reset_form"
                        onFinish={handleUpdatePassword}
                        layout="vertical"
                        requiredMark={false}
                        size="large"
                    >
                        <Form.Item
                            name="password"
                            label={<span style={{ fontWeight: '600', color: '#374151' }}>{intl.formatMessage({ id: 'auth.passwordReset.label.newPassword' })}</span>}
                            hasFeedback
                            rules={[
                                {
                                    required: true,
                                    message: intl.formatMessage({ id: 'auth.passwordReset.passwordRequired' }),
                                    min: 8
                                },
                            ]}
                        >
                            <Input.Password
                                prefix={<LockOutlined style={{ color: '#9ca3af' }} />}
                                placeholder={intl.formatMessage({ id: 'auth.passwordReset.newPasswordPlaceholder' })}
                                style={{ 
                                    height: isMobile ? '52px' : '48px',
                                    border: '2px solid #e5e7eb',
                                    fontSize: isMobile ? '16px' : '16px'
                                }}
                            />
                        </Form.Item>

                        <Form.Item
                            name="confirm"
                            label={<span style={{ fontWeight: '600', color: '#374151' }}>{intl.formatMessage({ id: 'auth.passwordReset.label.confirmPassword' })}</span>}
                            dependencies={['password']}
                            hasFeedback
                            rules={[
                                {
                                    required: true,
                                    message: intl.formatMessage({ id: 'auth.passwordReset.validation.confirmRequired' }),
                                },
                                ({ getFieldValue }) => ({
                                    validator(_, value) {
                                        if (!value || getFieldValue('password') === value) {
                                            return Promise.resolve();
                                        }
                                        return Promise.reject(new Error(intl.formatMessage({ id: 'auth.passwordReset.passwordMismatch' })));
                                    },
                                }),
                            ]}
                        >
                            <Input.Password
                                prefix={<LockOutlined style={{ color: '#9ca3af' }} />}
                                placeholder={intl.formatMessage({ id: 'auth.passwordReset.confirmPasswordPlaceholder' })}
                                style={{ 
                                    height: isMobile ? '52px' : '48px',
                                    border: '2px solid #e5e7eb',
                                    fontSize: isMobile ? '16px' : '16px'
                                }}
                            />
                        </Form.Item>

                        <Form.Item>
                            <Button
                                block
                                type="primary"
                                htmlType="submit"
                                loading={updatePasswordResult.isLoading}
                                style={{
                                    height: isMobile ? '52px' : '48px',
                                    borderRadius: '12px',
                                    background: 'linear-gradient(135deg, #F59E0B 0%, #D97706 100%)',
                                    border: 'none',
                                    fontSize: isMobile ? '16px' : '16px',
                                    fontWeight: '600',
                                    boxShadow: '0 4px 14px 0 rgba(245, 158, 11, 0.4)'
                                }}
                            >
                                {intl.formatMessage({ id: 'auth.passwordReset.updateButton' })}
                            </Button>
                        </Form.Item>

                        <div style={{ textAlign: 'center', marginTop: isMobile ? '1rem' : '1.5rem' }}>
                            <Text style={{ color: '#6b7280', fontSize: isMobile ? '14px' : '16px' }}>
                                {intl.formatMessage({ id: 'auth.passwordReset.problemsText' })}
                            </Text>
                            {" "}
                            <Link href="/forgot" style={{ color: '#F59E0B', fontWeight: '600', fontSize: isMobile ? '14px' : '16px' }}>
                                {intl.formatMessage({ id: 'auth.passwordReset.tryAgainLink' })}
                            </Link>
                        </div>
                    </Form>
                </>
            ) : (
                <div style={{ textAlign: 'center', padding: '2rem 0' }}>
                    <div style={{
                        fontSize: '64px',
                        marginBottom: '1.5rem',
                        color: '#F59E0B'
                    }}>
                        <CheckCircleOutlined />
                    </div>
                    <Title level={2} style={{ color: '#1f2937', fontWeight: '700', marginBottom: '1rem' }}>
                        {intl.formatMessage({ id: 'auth.passwordReset.successTitle' })}
                    </Title>
                    <Text style={{ color: '#6b7280', fontSize: '16px', lineHeight: '1.6' }}>
                        {intl.formatMessage({ id: 'auth.passwordReset.successText' })}
                    </Text>
                    <div style={{ marginTop: '2rem' }}>
                        <Link href="/sign-in" style={{
                            color: '#F59E0B',
                            fontWeight: '600',
                            fontSize: '16px'
                        }}>
                            {intl.formatMessage({ id: 'auth.passwordReset.signinPageLink' })}
                        </Link>
                        <Text style={{ color: '#6b7280', fontSize: '16px' }}>
                            {" "}{intl.formatMessage({ id: 'auth.passwordReset.successTextContinued' })}
                        </Text>
                    </div>
                </div>
            )}
        </ModernAuthLayout>
    );
}

export default ResetPasswordPage;